import { motion } from "framer-motion";
import { useState } from "react";
import { useConnectDispatch, useConnectState } from "../ConnectContext";
import ConnectStep from "../components/ConnectStep";
import { AnimatedCheckmark } from "../components/AnimatedCheckmark";
import { sendReconnectSuccess, sendSuccess } from "../messages";
import { ActionType } from "../types";

const FinishedStep = () => {
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const {
    institution,
    connectionId,
    accounts,
    user,
    scopedToken,
    appConfig,
    isReconnect,
  } = useConnectState();
  const dispatch = useConnectDispatch();

  const handleFinish = () => {
    if (!connectionId || !institution) throw Error("Missing data");

    // For reconnect, don't send accounts (they weren't reselected)
    if (isReconnect) {
      sendReconnectSuccess(connectionId, institution);
    } else {
      if (!accounts?.selected) throw Error("Missing accounts");
      sendSuccess(
        connectionId,
        accounts.selected,
        institution,
        user,
        scopedToken,
      );
    }

    setTimeout(() => {
      dispatch({ type: ActionType.Reset });
    }, 0);
  };

  const accountCount = accounts?.selected?.length || 0;

  return (
    <ConnectStep
      stepId="finished"
      nextButtonLabel="Finalizar"
      onClickNext={handleFinish}
    >
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        {/* Success animation */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 20,
            delay: 0.1,
          }}
        >
          <AnimatedCheckmark size={100} delay={0.2} />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="text-2xl font-semibold tracking-tight mt-6"
        >
          {isReconnect ? "Conexión restaurada" : "Conexión exitosa"}
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="text-muted-foreground mt-3 leading-relaxed max-w-xs"
        >
          {isReconnect ? (
            <>
              Tu cuenta bancaria de{" "}
              <span className="font-medium text-foreground">
                {institution?.name}
              </span>{" "}
              ha sido reconectada exitosamente a{" "}
              <span className="font-medium text-foreground">
                {appConfig?.name}
              </span>
              .
            </>
          ) : (
            <>
              {accountCount === 1 ? (
                <>
                  Tu cuenta de{" "}
                  <span className="font-medium text-foreground">
                    {institution?.name}
                  </span>{" "}
                  ha sido conectada a{" "}
                  <span className="font-medium text-foreground">
                    {appConfig?.name}
                  </span>
                  .
                </>
              ) : (
                <>
                  <span className="font-medium text-foreground">
                    {accountCount} cuentas
                  </span>{" "}
                  de{" "}
                  <span className="font-medium text-foreground">
                    {institution?.name}
                  </span>{" "}
                  han sido conectadas a{" "}
                  <span className="font-medium text-foreground">
                    {appConfig?.name}
                  </span>
                  .
                </>
              )}
            </>
          )}
        </motion.p>

        {/* Connected accounts summary (for non-reconnect) */}
        {!isReconnect && accounts?.selected && accounts.selected.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
            className="mt-6 w-full max-w-xs"
          >
            <div className="bg-muted/50 rounded-xl p-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Cuentas conectadas
              </p>
              <div className="space-y-1">
                {(showAllAccounts
                  ? accounts.selected
                  : accounts.selected.slice(0, 3)
                ).map((account, index) => (
                  <motion.div
                    key={account.id}
                    initial={index >= 3 ? { opacity: 0, height: 0 } : false}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{
                      duration: 0.2,
                      delay: index >= 3 ? (index - 3) * 0.05 : 0,
                    }}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-foreground truncate pr-2">
                      {account.name}
                    </span>
                    <span className="text-muted-foreground font-mono text-xs">
                      ****{account.lastFour}
                    </span>
                  </motion.div>
                ))}
                {accounts.selected.length > 3 && !showAllAccounts && (
                  <button
                    onClick={() => setShowAllAccounts(true)}
                    className="text-xs text-bank-primary hover:text-bank-primary/80 pt-1 transition-colors"
                  >
                    +{accounts.selected.length - 3} más
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </ConnectStep>
  );
};

export default FinishedStep;
